import { useState } from 'react';
import { api } from '@/lib/api';
import { formatCOP, formatDate } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { RefreshCw, Unlock, RotateCcw } from 'lucide-react';
import { Pill, Empty, useFetch, runAction } from './shared';

const FILTERS = [
  { key: 'blocked_missing_doc', label: 'Sin NIT/CC' },
  { key: 'blocked_quota_exceeded', label: 'Sin cupo' },
  { key: 'rejected_dian', label: 'Rechazadas' },
  { key: 'failed_max_retries', label: 'Reintentos agotados' },
  { key: 'sending', label: 'Enviando' },
  { key: 'draft', label: 'Borrador' },
  { key: 'accepted_dian', label: 'Aceptadas' },
];

const VOIDABLE = ['activation', 'plan', 'sponsorship'];

function InvoiceRow({ inv, onRetry, onVoid }) {
  return (
    <tr className="border-t border-slate-100 text-sm" data-testid={`dian-invoice-row-${inv.invoice_id}`}>
      <td className="py-2 pr-3 font-mono text-xs">{inv.invoice_id}</td>
      <td className="py-2 pr-3">{inv.invoice_kind}</td>
      <td className="py-2 pr-3">{inv.customer_snapshot?.name || inv.customer_id || '—'}</td>
      <td className="py-2 pr-3 font-mono">{formatCOP(inv.total || 0)}</td>
      <td className="py-2 pr-3"><Pill value={inv.status} /></td>
      <td className="py-2 pr-3 text-xs text-slate-600 max-w-xs truncate" title={inv.rejected_reason || inv.blocked_reason || ''}>
        {inv.rejected_reason || inv.blocked_reason || '—'}
      </td>
      <td className="py-2 pr-3 font-mono text-xs">{inv.invoice_number || formatDate(inv.created_at)}</td>
      <td className="py-2 space-x-1">
        {inv.status === 'accepted_dian' && VOIDABLE.includes(inv.invoice_kind) && (
          <Button size="sm" variant="outline" className="rounded-none h-7"
            data-testid={`dian-invoice-void-${inv.invoice_id}`} onClick={() => onVoid(inv.invoice_id)}>
            Anular (NC)
          </Button>
        )}
        {['rejected_dian', 'failed_max_retries'].includes(inv.status) && inv.invoice_kind !== 'sandbox_test' && (
          <Button size="sm" variant="outline" className="rounded-none h-7"
            data-testid={`dian-invoice-retry-${inv.invoice_id}`} onClick={() => onRetry(inv.invoice_id)}>
            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Revisar y reintentar
          </Button>
        )}
      </td>
    </tr>
  );
}

export function DianInvoicesPanel({ onChanged }) {
  const [status, setStatus] = useState('blocked_missing_doc');
  const { data, loading, load } = useFetch(`/admin/dian/invoices?status=${status}&limit=100`);
  const refresh = () => { load(); onChanged && onChanged(); };
  const revalidate = () => runAction(() => api.post('/admin/dian/invoices/revalidate-blocked'),
    'Revalidación ejecutada', refresh);
  const recover = () => runAction(() => api.post('/admin/dian/invoices/recover-quota-blocked'),
    'Facturas recuperadas a borrador', refresh);
  const retry = (id) => runAction(() => api.post(`/admin/dian/invoices/${id}/retry`),
    'Factura devuelta a borrador', refresh);
  const voidInv = (id) => {
    const reason = window.prompt('Motivo de la anulación (mín. 5 caracteres)');
    if (!reason || reason.trim().length < 5) return;
    runAction(() => api.post(`/admin/dian/invoices/${id}/void`, { reason: reason.trim() }),
      'Nota crédito de anulación creada en borrador', refresh);
  };
  const items = data?.items || [];
  return (
    <div className="space-y-3" data-testid="dian-invoices-panel">
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <Button key={f.key} size="sm" variant={status === f.key ? 'default' : 'outline'}
            className="rounded-none" data-testid={`dian-invoice-filter-${f.key}`} onClick={() => setStatus(f.key)}>
            {f.label}
          </Button>
        ))}
        <div className="flex-1" />
        <Button size="sm" variant="outline" className="rounded-none" data-testid="dian-revalidate-blocked-btn" onClick={revalidate}>
          <Unlock className="h-4 w-4 mr-1" /> Revalidar sin NIT/CC
        </Button>
        <Button size="sm" variant="outline" className="rounded-none" data-testid="dian-recover-quota-btn" onClick={recover}>
          <Unlock className="h-4 w-4 mr-1" /> Recuperar por cupo
        </Button>
        <Button size="sm" variant="ghost" className="rounded-none" data-testid="dian-invoices-reload" onClick={load}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      {items.length === 0 ? <Empty testId="dian-invoices-empty">No hay facturas en este estado.</Empty> : (
        <div className="overflow-x-auto border border-slate-200 bg-white p-3">
          <table className="w-full text-left" data-testid="dian-invoices-table">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500">
              <tr><th>ID</th><th>Tipo</th><th>Vendedor</th><th>Total</th><th>Estado</th><th>Motivo</th><th>Número</th><th /></tr>
            </thead>
            <tbody>{items.map((inv) => <InvoiceRow key={inv.invoice_id} inv={inv} onRetry={retry} onVoid={voidInv} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
